import cx from 'classnames'
import * as React from 'react'

import * as Money from '@mondough/money'
import type { Value } from '@mondough/money'

import VisuallyHidden from '../visually-hidden'
import styles from './money-input.module.scss'

export type TProps = {
  id: string
  className?: string
  placeholder?: string
  currencyCode: string
  disabled?: boolean
  invalid?: boolean
  required?: boolean
  onChange: (arg0: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (arg0: React.ChangeEvent<HTMLInputElement>) => void
  // we pass in a separate onFormatValue function so that we can format the money input correctly.
  onFormatValue?: (arg0: string | Value) => void
  large?: boolean
  // whether or not to use the currency-specific thousand and decimal separators.
  useCurrencySeparators?: boolean
  // hide fractional amounts they're zero (i.e. show £1 instead of £1.00, but always show £1.50 as £1.50)
  hideFractionalWhenZero?: boolean
  // should we allow negative values?
  allowNegative?: boolean
  // is this a negative value input?
  isNegative?: boolean
  // Do we want to store the money amount as a lib.money value, or a string? (default false)
  storeAsValue?: boolean
  value?: string
  name?: string
}

/* This has to be a controlled component, or the formatting won't work. */
export default function MoneyInput({
  className,
  id,
  currencyCode = 'GBP',
  disabled,
  invalid,
  placeholder,
  onChange,
  onBlur,
  onFormatValue,
  large = false,
  useCurrencySeparators,
  allowNegative,
  isNegative,
  storeAsValue = false,
  value,
  hideFractionalWhenZero,
  ...otherProps
}: TProps) {
  const currency = Money.getCurrencies()[currencyCode]
  const { symbolOnLeft, name, symbol } = currency ?? {}
  const formatValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (value === '') {
      return
    }
    try {
      let amountAsMoney = Money.newFromStringWithThousandsSeparator(
        currencyCode,
        value,
      )

      // If we don't want to allow negative values, force any accidental negative values (e.g. pasted in)
      // to be positive.
      if (
        allowNegative !== true &&
        amountAsMoney.lessThan(Money.fromMinorUnits(currencyCode, 0))
      ) {
        amountAsMoney = amountAsMoney.negated()
      }

      if (onFormatValue) {
        onFormatValue(
          storeAsValue
            ? amountAsMoney
            : amountAsMoney.formatAmount({
                useCurrencySeparators,
                hideFractionalWhenZero,
              }),
        )
      }
    } catch (err) {
      console.error(err)
      // don't try to format if invalid value, let the user correct it
      return null
    }
  }
  const onAmountChange = ({
    event,
    onChange,
  }: {
    event: React.ChangeEvent<HTMLInputElement>
    onChange: (arg0: React.ChangeEvent<HTMLInputElement>) => void
  }) => {
    const { value } = event.target
    // Prevent integer under/overflow
    if (value.length > 12) {
      return null
    }
    // don't accept any character that's not a number, minus sign at the start, space, decimal point or comma in a particular place.
    // this is hardcoded to GBP style right now (123,4567.00) - we may need to fix this up in future if we decide to accept other currencies
    // this regex technically lets you write 12,3456.00.... - users of this component will need to validate that.
    const validityRegex =
      allowNegative === true && isNegative !== true
        ? /^-?[\d,]*[.]?[\d]*$/
        : /^[\d,]+[.]?[\d]*$/
    if (value !== '' && !validityRegex.test(value)) {
      return null
    }

    // Call the original onChange handler
    onChange(event)
  }

  return (
    <div
      className={cx(
        styles['input-wrapper'],
        {
          [styles.invalid]: invalid,
          [styles.disabled]: disabled,
          [styles.large]: large,
        },

        className,
      )}
    >
      {isNegative === true && (
        <span className={cx(styles['negative-sign'])}>-</span>
      )}

      <div
        className={cx(styles['flex-wrapper'], {
          [styles['symbol-right']]: symbolOnLeft === false,
        })}
      >
        <div
          className={cx(styles.currency, {
            [styles['placeholder-shown']]: value == null || value === '',
          })}
        >
          <VisuallyHidden is="span">{name}</VisuallyHidden>
          <div className={styles['currency-symbol']} aria-hidden="true">
            {symbol}
          </div>
        </div>
        <input
          {...otherProps}
          value={value}
          disabled={disabled}
          id={id}
          className={cx(styles.input, {
            [styles.large]: large,
          })}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          onChange={(event) => onAmountChange({ event, onChange })}
          onBlur={(e) => {
            formatValue(e)
            if (onBlur) {
              onBlur(e)
            }
          }}
        />
      </div>
    </div>
  )
}
